import { ImageSourcePropType } from 'react-native';
import { IconName } from '../components/ui/Icon';

/**
 * The home screen's catalogue.
 *
 * Static seed data standing in for an API. Prices are whole rupees and
 * durations whole minutes, so nothing here has to parse or format on render.
 *
 * ------------------------------------------------------------------------
 * THIS IS NO LONGER EIGHT TRADES OF INVENTED SERVICES
 * ------------------------------------------------------------------------
 * This file used to hand-write 48 services across eight trades so Home always
 * had something to draw. Only two of them were ever things a customer could
 * actually book - the platform's real catalogue is what `GET /catalogue`
 * returns, and it currently sells exactly three services. Booking against
 * anything else fails server-side with "no such uuid", because the seed's
 * keys were never real ids.
 *
 * So this file now mirrors the platform's seed exactly - same category and
 * service names, prices and durations as `prisma/seed.ts` on the backend -
 * and is used ONLY as the offline fallback in `services/catalogue.ts` when
 * `GET /catalogue` cannot be reached, plus as the artwork/icon lookup for
 * whatever the live catalogue actually returns. It is not a wishlist of trades
 * the app might grow into; it is a snapshot of what is real today.
 *
 * ------------------------------------------------------------------------
 * ARTWORK
 * ------------------------------------------------------------------------
 * Photography lives in assets/uploads/services/tiles/ as `<category>_<n>.jpg`.
 * Those are cut from the eight contact sheets in the folder above - each sheet
 * is a titled poster holding a 2×3 grid of photographs, and the tiles are the
 * six photographs with the title and margins removed.
 *
 * Every service below points at the tile that actually shows the work it
 * describes, so the numbering is not arbitrary: re-cutting the sheets means
 * re-checking these.
 */

/**
 * A category's identity.
 *
 * A plain string since the catalogue started coming from the platform. It used
 * to be a union of the eight categories this file declares, and that union was
 * a promise the app could no longer keep: the server holds fourteen, it can
 * hold a fifteenth tomorrow, and a type that cannot express "Bathroom
 * Cleaning" is a type that forces the app to drop it.
 *
 * Offline it is still one of the keys below. Online it is the server's
 * slug - "plumbing-repairs", "ac-service". Nothing should switch on the value
 * except the artwork lookup in `services/catalogue.ts`, which is allowed to
 * miss and fall back to a glyph.
 */
export type CategoryKey = string;

export type Category = {
  key: CategoryKey;
  label: string;
  icon: IconName;
  /**
   * Still-life thumbnail for the category rail.
   *
   * Optional because two categories have no artwork yet - the rail falls back
   * to the category's icon, which reads as "not shot yet" rather than as a
   * mismatched stand-in.
   */
  image?: ImageSourcePropType;
  /**
   * The same trade as a still life - tools and products, nobody in shot.
   *
   * A second picture rather than a replacement. `image` is a cutout of a
   * professional, which is the right thing on Home where the tiles are people
   * you are about to hire. In a square tile it is a portrait in a box, and a
   * row of four portraits reads as a contact list; the objects read as trades.
   */
  tile?: ImageSourcePropType;
};

/**
 * How many categories the home grid shows before "View all".
 *
 * Historically six, to fill two rows of three with nothing ragged. The real
 * catalogue only has two categories today, so this cap is not currently
 * reached - it stays as the grid's intended row width for when the platform's
 * catalogue grows back out.
 */
export const HOME_CATEGORY_COUNT = 6;

/**
 * Which of this app's illustrated trades a category resembles.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NEEDED AT ALL
 * ---------------------------------------------------------------------------
 * The catalogue comes from the platform now, and the platform's categories are
 * its own: "plumbing-repairs", "bathroom-cleaning", "ac-service". This app was
 * written around eight trades of its own - `cleaning`, `plumbing`, … - and
 * three things are still keyed by those eight: the artwork, the icons, and the
 * how-it-works templates on a service's detail sheet. Only two of those eight
 * trades - `cleaning` and `appliance` - currently have real services behind
 * them; the rest stay mapped so a category the platform adds later still finds
 * a sensible template and glyph instead of nothing.
 *
 * Without a resolver those lookups return `undefined` for every server
 * category, which is exactly how the detail sheet came to crash on
 * `TEMPLATES[service.category].steps`.
 *
 * Exact match first - offline the category IS one of the eight. Then the known
 * server slugs. Then a keyword guess, so a category nobody has mapped yet
 * ("chimney-cleaning") still finds the cleaning template rather than nothing.
 * `null` only when even that misses, and every caller must handle it.
 */
const TRADE_BY_SLUG: Record<string, CategoryKey> = {
  'home-cleaning': 'cleaning',
  'deep-cleaning': 'cleaning',
  'bathroom-cleaning': 'cleaning',
  plumber: 'plumbing',
  'plumbing-repairs': 'plumbing',
  electrician: 'electrical',
  'electrical-repairs': 'electrical',
  'appliance-repair': 'appliance',
  'appliance-installation': 'appliance',
  'ac-service': 'appliance',
  carpenter: 'carpentry',
  'carpentry-repairs': 'carpentry',
  'carpentry-and-installation': 'carpentry',
  'pest-control': 'pest',
  'pest-treatment': 'pest',
  'salon-at-home': 'beauty',
  'wellness-at-home': 'wellness',
};

/** Last resort: read the words. Ordered, so the first match wins. */
const TRADE_BY_KEYWORD: Array<[RegExp, CategoryKey]> = [
  [/clean|wash|maid|sanit|chimney/, 'cleaning'],
  [/plumb|tap|pipe|basin|toilet|geyser|drain|water/, 'plumbing'],
  [/electric|wir|switch|socket|fan|light|mcb|fuse/, 'electrical'],
  /* 'ac' on its own would match 'surface' and 'terrace', so it is spelt
     out as the two ways the catalogue writes it. */
  [
    /appliance|ac service|air con|fridge|refriger|machine|microwave|purifier|geyser/,
    'appliance',
  ],
  [/carpent|furnitur|door|lock|shelf|curtain|hinge|wood/, 'carpentry'],
  [/pest|cockroach|termite|rodent|mosquito/, 'pest'],
  [/salon|beauty|hair|makeup|nail|facial|groom/, 'beauty'],
  [/wellness|spa|massage|yoga|therap/, 'wellness'],
];

/**
 * The trades this app still recognises by keyword and template, even though
 * only `cleaning` and `appliance` currently have a category and service in
 * `categories`/`services` below. Kept as the full eight so `tradeOf` and the
 * detail-sheet templates in `data/serviceDetail.ts` keep working for whatever
 * the live catalogue actually returns - a plumber category from the server
 * still finds the plumbing template, it simply has no seed artwork of its
 * own to borrow.
 */
const OWN_TRADES = new Set<CategoryKey>([
  'cleaning',
  'plumbing',
  'electrical',
  'appliance',
  'carpentry',
  'pest',
  'beauty',
  'wellness',
]);

export const tradeOf = (category: CategoryKey): CategoryKey | null => {
  if (OWN_TRADES.has(category)) {
    return category;
  }

  const mapped = TRADE_BY_SLUG[category];
  if (mapped) {
    return mapped;
  }

  const text = category.toLowerCase().replace(/-/g, ' ');
  const hit = TRADE_BY_KEYWORD.find(([pattern]) => pattern.test(text));
  return hit ? hit[1] : null;
};

/**
 * The two trades the platform actually sells today: Home Cleaning and
 * Appliance Repair, matching `prisma/seed.ts` on the backend exactly.
 */
export const categories: Category[] = [
  {
    key: 'cleaning',
    label: 'Home Cleaning',
    icon: 'sprayBottle',
    image: require('../../assets/uploads/services/objects/cleaning.png'),
    tile: require('../../assets/uploads/services/objects/cleaning.png'),
  },
  {
    key: 'appliance',
    label: 'Appliance Repair',
    icon: 'washer',
    image: require('../../assets/uploads/services/objects/appliance.png'),
    tile: require('../../assets/uploads/services/objects/appliance.png'),
  },
];

/** Rotating hints for the search field. Only trades the platform sells. */
export const searchHints: string[] = [
  'deep cleaning',
  'bathroom cleaning',
  'AC service',
];

/**
 * The hero that opens the home screen.
 *
 * One fixed block, not a rotating banner: it carries the top nav, so it cannot
 * page sideways underneath it. The artwork is composed with an empty left half
 * for the copy.
 */
export type HeroBanner = {
  key: string;
  /** Which category it drops you into; unset opens the whole catalogue. */
  category?: CategoryKey;
  eyebrow: string;
  headline: string;
  cta: string;
  image: ImageSourcePropType;
};

/**
 * The rotating banners under the search field.
 *
 * Each artwork is a room shot composed with its left half left as empty wall,
 * so the copy sits on the picture rather than in a column beside it. Keep any
 * new artwork to that composition - text laid over furniture is unreadable at
 * every width, which is what forced the previous hero to drop its headline.
 */
export const heroBanners: HeroBanner[] = [
  /*
   * The copy names what is in the picture behind it.
   *
   * These read as unrelated offers when the words and the artwork disagree -
   * re-shoot the artwork and this copy has to move with it.
   *
   * Every line is kept to about fifteen characters. The copy column is 54% of
   * the screen and the block is a fixed height that fits two lines of 22pt; a
   * line that wraps renders three and pushes the button onto the dots.
   */
  {
    // A sofa, a side table and a plant against a bare sunlit wall. No trade is
    // shown, so this one carries the general offer and opens the catalogue.
    key: 'first-booking',
    eyebrow: 'New to Homingo',
    headline: '25% off your\nfirst booking',
    cta: 'Book service',
    image: require('../../assets/uploads/banner/banner1.png'),
  },
  {
    // A basin with a mixer tap, rolled towels and soap. The towels and the
    // eucalyptus carry the clean; the tap is off to one side.
    key: 'cleaning',
    category: 'cleaning',
    eyebrow: 'Home cleaning',
    headline: 'Kitchen & bath\nmade spotless',
    cta: 'Book service',
    image: require('../../assets/uploads/banner/banner3.png'),
  },
];

export type Promo = {
  key: string;
  /**
   * Which category the banner drops you into. Left unset for the general
   * banners, which open the whole catalogue instead of narrowing it.
   */
  category?: CategoryKey;
  /** The offer itself, in as few words as it can be said. */
  headline: string;
  /** What it applies to. One short line. */
  subhead: string;
  /** Promo code, shown as a chip. Omit for offers that need no code. */
  code?: string;
  /** Drawn on the right of the card. */
  icon: IconName;
  /** Spoken by screen readers in place of the chevron. */
  cta: string;
};

export const promos: Promo[] = [
  {
    key: 'first',
    headline: 'Flat ₹100 OFF',
    subhead: 'on your first service',
    code: 'HOMINGO100',
    icon: 'gift',
    cta: 'Claim offer',
  },
  {
    key: 'deep-clean',
    category: 'cleaning',
    headline: '20% OFF deep cleaning',
    subhead: 'this weekend only',
    code: 'SPARKLE20',
    icon: 'sprayBottle',
    cta: 'Book a clean',
  },
  {
    key: 'inspection',
    category: 'appliance',
    headline: 'Free inspection',
    subhead: 'on any appliance repair',
    icon: 'washer',
    cta: 'Book a repair',
  },
];

export type Service = {
  key: string;
  category: CategoryKey;
  name: string;
  /** One line on what is actually done. */
  blurb: string;
  /**
   * What the visit includes, as separate lines.
   *
   * The platform sends `Service.description` as a fragment of HTML - a `<p>`
   * summary followed by a `<ul>` of inclusions - so the two halves arrive in
   * one field. `blurb` carries the paragraph; this carries the list items,
   * already unwrapped. See `parseDescription` in `services/catalogue.ts`.
   *
   * Optional because the seeded services below predate the field and a service
   * whose description is a bare sentence genuinely has no list. Empty and
   * absent mean the same thing to every screen that reads it.
   */
  includes?: string[];
  /**
   * What the visit does NOT cover, as separate lines.
   *
   * From `Service.notIncluded` on the platform - its own rich-text column,
   * not folded into the description the way `includes` is. Both arrive as a
   * bullet list and are unwrapped by `parseScopeList` in
   * `services/catalogue.ts`.
   *
   * Optional, and usually absent for a while: ops fills these in service by
   * service, and `detailFor` falls back to the per-category text for whatever
   * has not been written yet. Empty and absent mean the same thing.
   */
  excludes?: string[];
  /**
   * The questions on this service's sheet, as the platform sent them.
   *
   * Already resolved - the server decides whether each one came from its
   * shared library or was written for this service alone, and sends the
   * question and answer either way. This app is deliberately not told which,
   * so the library can be reshaped without a release.
   */
  faqs?: { question: string; answer: string }[];
  price: number;
  /**
   * The struck-through figure beside {@link price} - "~~₹1,000~~ ₹600".
   *
   * ---------------------------------------------------------------------------
   * NOBODY IS EVER CHARGED THIS
   * ---------------------------------------------------------------------------
   * It is not a former price and not a discount: no bill carries it, no
   * booking is made at it, and no saving is computed from it. {@link price} is
   * the only number this app may add to a cart, a total or a quote.
   *
   * So it belongs on the two surfaces that sell - the service card and the
   * service sheet - and on **neither the cart nor the checkout**. There the
   * figure that matters is the one being paid, and a struck-through number
   * beside it would claim a reduction the bill does not show.
   *
   * Absent on almost every service, which is the ordinary state: the card
   * draws one price. Always greater than {@link price} when present - the
   * server enforces it, and `toService` drops anything lower rather than
   * render a card that reads as a price rise.
   */
  wasPrice?: number;
  /**
   * Out of 5 - or absent, which is not the same as zero.
   *
   * Optional since the catalogue started coming from the platform. The server
   * holds names, prices and durations; it has no ratings, so a service the app
   * has never seen arrives without one. The cards omit the row rather than
   * printing a 0.0 or, worse, an invented 4.8 on a service nobody has booked.
   */
  rating?: number;
  /** Review count, already abbreviated for display. Absent with `rating`. */
  reviews?: string;
  minutes: number;
  image?: ImageSourcePropType;
  /**
   * The same trade as a still life - tools and products, nobody in shot.
   *
   * A second picture rather than a replacement. `image` is a cutout of a
   * professional, which is the right thing on Home where the tiles are people
   * you are about to hire. In a square tile it is a portrait in a box, and a
   * row of four portraits reads as a contact list; the objects read as trades.
   */
  tile?: ImageSourcePropType;
  /**
   * Which booking flows this service actually allows - set by the platform,
   * not by the app. Undefined on the bundled seed (there is no server row to
   * ask), which the cart's mode picker treats as "no restriction known" so an
   * offline session is not blocked from booking anything.
   *
   * Real services always carry all three: a service the backend does not
   * support any mode for is not sellable, and would not be in the catalogue
   * at all.
   */
  supportsInstant?: boolean;
  supportsScheduled?: boolean;
  supportsRecurring?: boolean;
  /**
   * How this service is sold - set by the platform.
   *
   * `fixed` is one price for the job, which is every service in the
   * catalogue as it stands and the way this app has always worked.
   *
   * `hourly` means the customer buys a span of a Pro's time and chooses
   * how long. On one of those, **`price` below is the rate for ONE HOUR,
   * not the price of the job**, and `minutes` is one hour rather than the
   * length of the visit - both only become the real figures once the hours
   * are picked. Nothing may draw either of them bare on an hourly service;
   * `data/hourly.ts` holds the arithmetic every screen shares.
   *
   * Undefined on the bundled seed and from an older backend, and read as
   * `fixed` - which is what those services are.
   */
  pricingModel?: 'fixed' | 'hourly';
  /**
   * The hours a customer may buy, inclusive. Both present on an hourly
   * service (the platform refuses to activate one without them) and both
   * absent otherwise.
   */
  minHours?: number;
  maxHours?: number;
  /**
   * How many of this service one booking (one basket) may hold - the
   * platform's `maxQuantityPerBooking`, set per service by ops. The basket
   * steppers stop here and the checkout refuses past it. Absent on the
   * bundled seed and from an older backend - read it through `maxPerBooking`.
   */
  maxPerBooking?: number;
};

/** The platform's default per-booking cap, for a service that does not carry one. */
export const DEFAULT_MAX_PER_BOOKING = 5;

/** A service's per-booking cap as a whole number of at least one. */
export const maxPerBooking = (service: Service): number =>
  typeof service.maxPerBooking === 'number' &&
  Number.isFinite(service.maxPerBooking) &&
  service.maxPerBooking >= 1
    ? Math.floor(service.maxPerBooking)
    : DEFAULT_MAX_PER_BOOKING;

/**
 * The three services the platform actually sells. Names, descriptions
 * (folded into `blurb`), prices and durations match `prisma/seed.ts` on the
 * backend exactly, so `services/catalogue.ts`'s name-matching finds these
 * when the server returns the same three rows.
 */
/**
 * The ratings and reviews in this file are invented, and nothing shows them.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS FABRICATED
 * ---------------------------------------------------------------------------
 * `Service.rating` and `Service.reviews` below - 4.78, "860K" - were written
 * by hand so the screens would look finished. `ServiceDetail.breakdown` and
 * `ServiceDetail.reviews` in `serviceDetail.ts` are the same thing: a star
 * histogram, and a list of named customers with review text nobody wrote.
 *
 * The platform has no ratings. Nothing aggregates them and no endpoint returns
 * them.
 *
 * ---------------------------------------------------------------------------
 * WHY A FLAG RATHER THAN A DELETION
 * ---------------------------------------------------------------------------
 * These have stayed off screen only by accident. A live service picks up this
 * data by matching its NAME, and none of the fifteen services the platform
 * actually sells has a name in this file - so `rating` is undefined today and
 * every block that draws it is skipped. A rename, or activating one of the
 * three drafts that ARE in here, would put an invented rating and invented
 * reviews in front of a customer, with nobody having done anything wrong.
 *
 * So the wire is cut in one place, named, with the reason attached. When real
 * reviews exist, delete this constant and the guards that reference it; the
 * markup on every screen is intact and will work again.
 *
 * Annotated `boolean` rather than left to infer `true`: the literal type
 * makes TypeScript treat the guarded branches as unreachable, and it then
 * refuses to narrow `rating` inside them.
 *
 * NOT to be confused with `data/reviews.ts`, `RatingsScreen` or
 * `ReviewSheet`. Those are the customer's own reviews of jobs they booked,
 * they are real, and nothing here touches them.
 */
export const RATINGS_ARE_INVENTED: boolean = true;

export const services: Service[] = [
  // ------------------------------------------------------------- cleaning
  {
    key: 'clean-full',
    category: 'cleaning',
    name: 'Full Home Deep Cleaning (2 BHK)',
    blurb: 'End-to-end deep clean of a 2 BHK, including kitchen and bathrooms.',
    price: 4999,
    rating: 4.78,
    reviews: '860K',
    minutes: 240,
    image: require('../../assets/uploads/services/tiles/cleaning_6.jpg'),
  },
  {
    key: 'clean-bath',
    category: 'cleaning',
    name: 'Bathroom Deep Clean',
    blurb: 'Single bathroom, descaling and sanitisation included.',
    price: 699,
    rating: 4.82,
    reviews: '3.9M',
    minutes: 60,
    image: require('../../assets/uploads/services/tiles/cleaning_5.jpg'),
  },

  // ------------------------------------------------------------ appliance
  {
    key: 'appl-ac',
    category: 'appliance',
    name: 'Split AC Service',
    blurb: 'Wet service of one split AC unit, filter and coil cleaning.',
    price: 599,
    rating: 4.7,
    reviews: '520K',
    minutes: 90,
    image: require('../../assets/uploads/services/tiles/appliance_2.jpg'),
  },
];

/**
 * Trust strip under the fold.
 *
 * "Up to 30 Days Warranty" was the third cell and has been removed at Vishal's
 * request. The strip is a row of `flex: 1` cells, so it re-spaces itself -
 * three sit as evenly as four did, and adding one back needs no layout change.
 *
 * Every line here is a promise the platform has to honour in front of a
 * customer holding the app. Add a fourth only when that is true of it.
 */
export const guarantees: Array<{ icon: IconName; label: string }> = [
  { icon: 'shieldCheck', label: 'Verified\nProfessionals' },
  { icon: 'creditCard', label: 'Transparent\nPricing' },
  { icon: 'pin', label: 'Live\nTracking' },
];


/** The first-booking coupon shown under the categories. */
export const firstBookingOffer = {
  amount: 'Flat ₹100 OFF',
  detail: 'on your first service',
  code: 'HOMINGO100',
};

/**
 * What leads the "Popular Services" rail.
 *
 * A hand-picked pair rather than a computed "most booked" - there is no
 * booking history to compute from, and the point of the rail is to show one
 * strong service from each of the real categories rather than repeats of one.
 */
export const popularServiceKeys = ['clean-bath', 'appl-ac'] as const;

export const popularServices = (): Service[] =>
  popularServiceKeys
    .map(key => services.find(service => service.key === key))
    .filter((service): service is Service => Boolean(service));


/**
 * Subcategories - the step between a category and a list of services.
 *
 * Membership lives here as a list of service keys rather than as a field on
 * each service. Both express the same thing, but keeping it in one place makes
 * the grouping readable as a whole: you can see that every service has a home
 * and that none is listed twice.
 */
/**
 * A run of services under one heading inside a subcategory listing.
 *
 * Some subcategories join two different kinds of work under one name -
 * "Kitchen & bathroom" is a shelf, not a service, and putting a kitchen deep
 * clean and a bathroom deep clean under a single heading tells someone they
 * are variants of one thing. Splitting them lets each half be scanned on its
 * own.
 *
 * Optional: most subcategories are one kind of work and read better as a
 * single run. `sectionsInSub` falls back to one section named after the
 * subcategory when this is not set.
 */
export type ServiceSection = {
  title: string;
  serviceKeys: string[];
};

export type SubCategory = {
  key: string;
  category: CategoryKey;
  label: string;
  /** One line on what falls under it. */
  blurb: string;
  /**
   * Every service under this subcategory, in listing order.
   *
   * Stays the single source of membership even when `sections` is set - the
   * sections re-group these keys, they do not add to them, and `sectionsInSub`
   * checks that the two agree.
   */
  serviceKeys: string[];
  /** Headings to split `serviceKeys` under. Omit to keep one run. */
  sections?: ServiceSection[];
};

/**
 * The platform's real subcategories: Deep Cleaning and Bathroom Cleaning
 * under Home Cleaning, AC Service under Appliance Repair - matching
 * `prisma/seed.ts` exactly.
 */
export const subCategories: SubCategory[] = [
  // ------------------------------------------------------------- cleaning
  {
    key: 'clean-home',
    category: 'cleaning',
    label: 'Deep Cleaning',
    blurb: 'Full-home deep clean visits',
    serviceKeys: ['clean-full'],
  },
  {
    key: 'clean-wet',
    category: 'cleaning',
    label: 'Bathroom Cleaning',
    blurb: 'Descaling and sanitisation',
    serviceKeys: ['clean-bath'],
  },

  // ------------------------------------------------------------ appliance
  {
    key: 'appl-ac',
    category: 'appliance',
    label: 'AC Service',
    blurb: 'Wet service, filter and coil cleaning',
    serviceKeys: ['appl-ac'],
  },
];

/** Subcategories of one category, in order. */
export const subCategoriesIn = (category: CategoryKey): SubCategory[] =>
  subCategories.filter(sub => sub.category === category);

/**
 * Which band of a category each subcategory belongs to.
 *
 * Kept as a lookup rather than a field on each entry: the split is
 * presentation - how the picker is banded - not a property of the
 * subcategory itself.
 *
 * Anything unlisted falls into `DEFAULT_BAND`, so a new subcategory still
 * appears rather than vanishing from the picker.
 */
const BANDS: Record<string, string> = {
  'clean-home': 'Cleaning',
  'clean-wet': 'Cleaning',
};

const DEFAULT_BAND = 'Services';

export type SubCategoryBand = {
  title: string;
  subs: SubCategory[];
};

/**
 * A category's subcategories, banded for the picker and in first-seen order.
 *
 * Insertion order rather than alphabetical: the catalogue is already ordered
 * with the common work first, and sorting the bands would put "Add-ons" above
 * "Cleaning".
 */
export const subCategoryBands = (category: CategoryKey): SubCategoryBand[] => {
  const bands: SubCategoryBand[] = [];

  subCategoriesIn(category).forEach(sub => {
    const title = BANDS[sub.key] ?? DEFAULT_BAND;
    const existing = bands.find(band => band.title === title);
    if (existing) {
      existing.subs.push(sub);
    } else {
      bands.push({ title, subs: [sub] });
    }
  });

  return bands;
};

/** Services under one subcategory, in catalogue order. */
export const servicesInSub = (sub: SubCategory): Service[] =>
  sub.serviceKeys
    .map(key => services.find(service => service.key === key))
    .filter((service): service is Service => Boolean(service));

/** A section with its services resolved, ready to render. */
export type ResolvedSection = {
  title: string;
  services: Service[];
};

/**
 * A subcategory's services, split under the headings it declares.
 *
 * A subcategory with no `sections` comes back as one run named after itself,
 * so the listing has a single shape to render either way.
 *
 * Anything in `serviceKeys` that no section claims is appended under the
 * subcategory's own name rather than dropped. Silently losing a service
 * because someone added it to `serviceKeys` and forgot the section is the one
 * failure here that would never be noticed.
 */
export const sectionsInSub = (sub: SubCategory): ResolvedSection[] => {
  const all = servicesInSub(sub);

  if (!sub.sections?.length) {
    return [{ title: sub.label, services: all }];
  }

  const claimed = new Set<string>();
  const sections: ResolvedSection[] = sub.sections.map(section => {
    const items = section.serviceKeys
      .map(key => all.find(service => service.key === key))
      .filter((service): service is Service => Boolean(service));
    items.forEach(service => claimed.add(service.key));
    return { title: section.title, services: items };
  });

  const rest = all.filter(service => !claimed.has(service.key));
  if (rest.length) {
    sections.push({ title: sub.label, services: rest });
  }

  return sections.filter(section => section.services.length > 0);
};

/** Services belonging to a category, in catalogue order. */
export const servicesIn = (category: CategoryKey): Service[] =>
  services.filter(service => service.category === category);

/**
 * Which subcategory a service sits under.
 *
 * Membership is stored the other way round - each subcategory lists its
 * services - so this walks them. Three entries makes that cheap enough not
 * to warrant an index, and one index fewer is one fewer thing to keep in
 * step when the catalogue moves.
 *
 * Undefined is possible in principle but not in practice: every one of the
 * three services is claimed by exactly one subcategory. Callers should still
 * handle it rather than assert.
 */
export const subCategoryOf = (service: Service): SubCategory | undefined =>
  subCategories.find(sub => sub.serviceKeys.includes(service.key));

/**
 * Services worth suggesting next to a basket.
 *
 * Drawn from the subcategories already in the cart, so the suggestions are
 * things that go with what was chosen rather than whatever is popular - a
 * bathroom clean should offer a kitchen clean, not a massage. Anything already
 * in the cart is excluded, or the rail would offer what is sitting above it.
 *
 * Falls back to the popular list when the cart's own subcategories are
 * exhausted, so the rail is never one lonely card.
 */
export const similarServices = (
  inCart: Service[],
  limit: number = 6,
): Service[] => {
  const have = new Set(inCart.map(service => service.key));

  const nearby: Service[] = [];
  inCart.forEach(service => {
    const sub = subCategoryOf(service);
    if (!sub) {
      return;
    }
    servicesInSub(sub).forEach(candidate => {
      if (!have.has(candidate.key) && !nearby.includes(candidate)) {
        nearby.push(candidate);
      }
    });
  });

  popularServices().forEach(candidate => {
    if (!have.has(candidate.key) && !nearby.includes(candidate)) {
      nearby.push(candidate);
    }
  });

  return nearby.slice(0, limit);
};
